import React, { useMemo } from "react";

import { Tooltip } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useOperationDespatchContext } from "../../../../../../../../contexts/DespatchContext";
import MilitaryBadge from "Components/Generic/Icons/MilitaryBadge";
import { StarSlash } from "iconsax-react";
import { MemberAvailability } from "Models/members/member.availability";
import { useTranslation } from "react-i18next";
import { checkAvailability } from "Util/checkAvailability";
import { CertificationType } from "Util/enums";

import { ResourceIcons, ResourceIconsProps } from "../ResourceIcons";
import {
  Card,
  CardIcon,
  CardInfosWrapper,
  CertificationsTypeContainer,
  CertificationsTypeWrapper,
  Details,
  Image,
  PlainText,
  SubTitle,
  UnavailableText,
} from "../styles";
import { SubContractorBadge } from "../SubContractorBadge";
import { useAppState } from "Context/StateContext";

interface ResourceMemberCardProps {
  isSubcontractor: boolean;
  actualDate: Date;
  name: string;
  iconName?: ResourceIconsProps["name"];
  icon?: React.ReactNode;
  imgSrc?: any;
  tasks?: { id: number; type: string; jobReference: string }[];
  showTasks?: boolean;
  availability?: MemberAvailability[];
  crewNamesRelated?: string[];
  certificationsType: CertificationType[];
}

export const ResourceMemberCard: React.FC<ResourceMemberCardProps> = ({
  imgSrc,
  icon,
  iconName,
  name,
  tasks,
  isSubcontractor,
  showTasks = true,
  availability,
  actualDate,
  crewNamesRelated,
  certificationsType,
}) => {
  const { t: translate } = useTranslation();
  const { formatWithUserTimezone, userTimezone } = useAppState();

  const { showUnavailableItems } = useOperationDespatchContext();

  const result = checkAvailability(actualDate, availability, userTimezone?.name);

  const numberOfTasks = useMemo(() => {
    return tasks
      ? `${tasks.length} ${tasks.length === 1 ? translate("task") : translate("tasks")}`
      : `${translate("notAllocated")}`;
  }, [tasks]);

  const tasksDescriptions = useMemo(() => {
    return tasks?.map((task) => `#${task.jobReference} - ${task.type}`).join(", ");
  }, [tasks]);

  if (!showUnavailableItems && !result.isAvailable) {
    return null;
  }

  const renderIcon = () => {
    if (!icon && !imgSrc && !iconName) return;

    const hasVIPCertificate = certificationsType.includes(CertificationType.VIP);
    const hasMilitaryCertificate = certificationsType.includes(CertificationType.MILITARY);

    return (
      <CardIcon hasCertification={hasVIPCertificate || hasMilitaryCertificate}>
        {icon}
        {imgSrc && <Image src={imgSrc} alt={name} />}
        <CertificationsTypeContainer>
          <CertificationsTypeWrapper>
            <div>{hasMilitaryCertificate && <MilitaryBadge height="16" width="16" />}</div>
            <div>
              {hasVIPCertificate && (
                <StarSlash size={16} color={colors.rose800} fill={colors.white} variant="Bold" />
              )}
            </div>
          </CertificationsTypeWrapper>
        </CertificationsTypeContainer>
        {iconName && <ResourceIcons name={iconName} size={24} />}
      </CardIcon>
    );
  };

  const renderTask = () => {
    if (!showTasks) return;

    if (result.unavailableHours) {
      return (
        <UnavailableText isAvailable={result.isAvailable}>
          {translate("unavailableOn")} {result.unavailableHours.date} {translate("between")}{" "}
          {result.unavailableHours.from} {translate("and")} {result.unavailableHours.to}
        </UnavailableText>
      );
    }

    if (result.isAvailable) {
      if (crewNamesRelated?.length) {
        return (
          <Tooltip title={tasksDescriptions} disableInteractive>
            <SubTitle>{crewNamesRelated.join(", ")}</SubTitle>
          </Tooltip>
        );
      }

      return (
        <Tooltip title={tasksDescriptions} disableInteractive>
          <SubTitle isEmpty={!tasks || tasks.length === 0}>{numberOfTasks}</SubTitle>
        </Tooltip>
      );
    }

    return (
      <UnavailableText>
        {`${translate("unavailableUntil")} ${
          result.nextAvailable && formatWithUserTimezone(result.nextAvailable, "dd MMM")
        }`}
      </UnavailableText>
    );
  };

  return (
    <Card>
      {renderIcon()}

      <CardInfosWrapper>
        <Details>
          <PlainText>{name}</PlainText>
          {renderTask()}
        </Details>

        {!!isSubcontractor && <SubContractorBadge />}
      </CardInfosWrapper>
    </Card>
  );
};
